import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useLiveQuery } from '@tanstack/react-db'

import { WhatsAppSendingSwitch } from '@/components/WhatsAppSendingSwitch'
import { FollowUpCategoriesSection } from '@/screens/FollowUpCategoriesSection'
import { useAuth } from '@/lib/auth'
import { useDb } from '@/lib/dbContext'
import { WhatsAppTemplateConfigSection } from '@/screens/WhatsAppTemplateConfigSection'
import {
  SettingsScreen,
  type SettingsBrandingValues,
  type SettingsTwilio,
  type SettingsTwilioValues,
} from '@/screens/SettingsScreen'

function SettingsNotice(props: { children: React.ReactNode; tone?: 'muted' | 'error' }) {
  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="max-w-container mx-auto space-y-3 px-4 py-6">
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className={`text-[13px] ${props.tone === 'error' ? 'text-rose-600' : 'text-slate-500'}`}>
          {props.children}
        </p>
        <Link to="/" className="text-[13px] font-medium text-blue-700 hover:underline">
          Back to bookings
        </Link>
      </main>
    </div>
  )
}

function AdminsOnly() {
  return (
    <SettingsNotice>
      This page is for admins only. Ask an admin to tick <code>is_admin</code> on
      your user record if you need access.
    </SettingsNotice>
  )
}

/** Per-section save lifecycle: pending flag plus inline error/success copy. */
function useSectionSave(defaultError: string) {
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const run = async (work: () => Promise<void>, successCopy: string) => {
    setIsSaving(true)
    setErrorMessage(null)
    setSuccessMessage(null)
    try {
      await work()
      setSuccessMessage(successCopy)
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message ? error.message : defaultError,
      )
    } finally {
      setIsSaving(false)
    }
  }

  return { isSaving, errorMessage, successMessage, run }
}

export function SettingsScreenContainer() {
  const db = useDb()
  const { user } = useAuth()
  const isAdmin = Boolean(user?.isAdmin)
  const brandingResult = useLiveQuery((q) =>
    q.from({ branding: db.brandingCollection }),
  )
  const settingsResult = useLiveQuery((q) =>
    q.from({ settings: db.appSettingsCollection }),
  )
  const brandingSave = useSectionSave('Could not save branding. Please try again.')
  const twilioSave = useSectionSave('Could not save the Twilio settings. Please try again.')
  const sendDelaySave = useSectionSave('Could not save the messaging settings. Please try again.')

  // undefined = loading, null = failed to load
  const [twilio, setTwilio] = useState<SettingsTwilio | null | undefined>(undefined)

  useEffect(() => {
    void db.brandingCollection.preload?.()
    void db.appSettingsCollection.preload?.()
  }, [db])

  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    db.loadWhatsAppTwilioSettings()
      .then((settings) => {
        if (!cancelled) setTwilio(settings)
      })
      .catch(() => {
        if (!cancelled) setTwilio(null)
      })
    return () => {
      cancelled = true
    }
  }, [db, isAdmin])

  if (!user) return null
  if (!isAdmin) return <AdminsOnly />

  const branding = brandingResult.data?.[0]
  if (!branding) {
    if (brandingResult.isError) {
      return (
        <SettingsNotice tone="error">
          Could not load the branding settings. Reload the page to try again.
        </SettingsNotice>
      )
    }
    if (brandingResult.isReady) {
      return (
        <SettingsNotice tone="error">
          No branding record found — the <code>branding</code> migration has not
          been applied on this install.
        </SettingsNotice>
      )
    }
    return <SettingsNotice>Loading…</SettingsNotice>
  }

  const appSettings = settingsResult.data?.[0]

  const handleSaveBranding = (values: SettingsBrandingValues) =>
    brandingSave.run(() => Promise.resolve(db.saveBranding(values)), 'Branding saved.')

  const handleSaveTwilio = (values: SettingsTwilioValues) =>
    twilioSave.run(async () => {
      const saved = await db.saveWhatsAppTwilioSettings(values)
      setTwilio(saved)
    }, 'Twilio settings saved.')

  const handleSaveSendDelay = (seconds: number) =>
    sendDelaySave.run(
      () => db.setWhatsAppSendDelay(seconds),
      'Messaging settings saved.',
    )

  return (
    <SettingsScreen
      branding={{
        name: branding.name,
        logoUrl: branding.logoUrl,
        logoOnLightUrl: branding.logoOnLightUrl,
      }}
      onSaveBranding={handleSaveBranding}
      isSaving={brandingSave.isSaving}
      errorMessage={brandingSave.errorMessage}
      successMessage={brandingSave.successMessage}
      twilio={twilio}
      onSaveTwilio={handleSaveTwilio}
      isSavingTwilio={twilioSave.isSaving}
      twilioErrorMessage={twilioSave.errorMessage}
      twilioSuccessMessage={twilioSave.successMessage}
      followUpCategoriesSection={<FollowUpCategoriesSection />}
      whatsappSendingSwitch={<WhatsAppSendingSwitch />}
      whatsappTemplatesSection={<WhatsAppTemplateConfigSection />}
      whatsappSendDelaySeconds={appSettings?.whatsappSendDelaySeconds}
      onSaveSendDelay={handleSaveSendDelay}
      isSavingSendDelay={sendDelaySave.isSaving}
      sendDelayErrorMessage={sendDelaySave.errorMessage}
      sendDelaySuccessMessage={sendDelaySave.successMessage}
    />
  )
}
